/**
 * KnowledgePanel — admin UI for ingesting scientific documents into the knowledge base.
 *
 * Design D-ING: admin panel → POST /admin/knowledge/ingest → worker chunks → SA writes.
 *
 * Features:
 *   - Upload form: document title + full text content.
 *   - Optional sourceUrl field (for provenance of web-sourced documents).
 *   - Calls POST /admin/knowledge/ingest with admin Bearer token.
 *   - Success state: shows the number of chunks written.
 *   - Error state: shows user-facing error message via aria-live alert.
 *   - Loading state: submit button disabled while in flight.
 *   - Empty guard: submit disabled when title or text is empty.
 *
 * Security:
 *   - Bearer token from deps.getIdToken() — admin claim enforced server-side.
 *   - textContent only — no dangerouslySetInnerHTML anywhere.
 *
 * Accessibility (CLAUDE.md §7):
 *   - Semantic form with labelled inputs.
 *   - aria-live alert on error.
 *   - role="status" on success.
 *   - 44px minimum touch targets on buttons.
 *
 * RENDER GATE: AdminPanel mounts this only for admin users (frontend defense-in-depth).
 * The Worker independently enforces the admin claim on every ingest request.
 */

import { useState } from "react";
import type { AdminHooksDeps } from "./hooks.js";

export interface KnowledgePanelProps {
  deps: AdminHooksDeps;
}

interface IngestResult {
  docId: string;
  chunksWritten: number;
}

export function KnowledgePanel({ deps }: KnowledgePanelProps): JSX.Element {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IngestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = title.trim().length > 0 && content.trim().length > 0 && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const idToken = await deps.getIdToken();
      if (!idToken) throw new Error("No ID token available.");

      const fetchImpl = deps.fetchImpl ?? fetch;
      const base = deps.workerUrl.replace(/\/+$/, "");

      const payload: Record<string, string> = {
        docId: title.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
        title: title.trim(),
        text: content.trim(),
      };
      if (sourceUrl.trim().length > 0) {
        payload["sourceUrl"] = sourceUrl.trim();
      }

      const res = await fetchImpl(`${base}/admin/knowledge/ingest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`ingest_error:${res.status}`);
      }

      const data = (await res.json()) as IngestResult;
      setResult(data);
      // Clear the form on success
      setTitle("");
      setContent("");
      setSourceUrl("");
    } catch {
      setError("No se pudo ingestar el documento. Verificá que tenés permisos de administrador.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section aria-label="knowledge-ingestion" className="space-y-5">
      <h2 className="text-base font-semibold text-zinc-100">Base de Conocimiento</h2>
      <p className="text-xs text-zinc-400">
        Subí documentos científicos (guías, papers, referencias) para enriquecer las respuestas del agente.
        Cada documento se divide en fragmentos y se almacena en Firestore.
      </p>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-400">Título del documento</span>
          <input
            type="text"
            aria-label="Título"
            className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
            value={title}
            placeholder="Ej: Guía Nutricional CESNID 2024"
            disabled={loading}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-400">Contenido del documento</span>
          <textarea
            aria-label="Contenido"
            rows={8}
            className="resize-y rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
            value={content}
            placeholder="Pegá el contenido completo del documento aquí…"
            disabled={loading}
            onChange={(e) => setContent(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-400">URL de origen (opcional)</span>
          <input
            type="url"
            aria-label="URL de origen"
            className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
            value={sourceUrl}
            placeholder="https://ejemplo.com/documento"
            disabled={loading}
            onChange={(e) => setSourceUrl(e.target.value)}
          />
        </label>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            aria-label="Ingestar documento"
            disabled={!canSubmit}
            className="min-h-[44px] rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
          >
            {loading ? "Ingrestando…" : "Ingestar documento"}
          </button>

          {loading ? (
            <span
              role="status"
              aria-live="polite"
              className="flex items-center gap-2 text-xs text-zinc-400"
            >
              <span
                aria-hidden="true"
                className="h-3 w-3 animate-spin rounded-full border-2 border-zinc-600 border-t-blue-400"
              />
              Procesando…
            </span>
          ) : null}
        </div>
      </form>

      {/* Success message */}
      {result != null ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-blue-800/40 bg-blue-950/20 px-4 py-3 text-sm"
        >
          <p className="font-medium text-blue-300">Documento ingresado con éxito.</p>
          <p className="mt-1 text-xs text-zinc-400">
            {/* textContent only — number rendered by React, not innerHTML */}
            Documento: <span className="text-zinc-200">{result.docId}</span>
            {" · "}
            Fragmentos escritos: <span className="text-zinc-200">{result.chunksWritten}</span>
          </p>
        </div>
      ) : null}

      {/* Error message */}
      {error != null ? (
        <p
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-red-900/50 bg-red-950/20 px-4 py-2 text-sm text-red-400"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}
